/*
 * Replicate sets (חזרות; docs/ANALYSIS_WORKBENCH.md 10f): dishes, or plates,
 * of ONE isolate on ONE medium at ONE temperature are replicates of each
 * other, grouped where they are shown -- nothing stores a set (the user's
 * decision R1, 2026-10-27). Pure: the tray (components/culture/TrayMode.js)
 * groups its dishes live, the dossier's growth (components/culture/
 * GrowthPanel.js) its plates.
 *
 *   setKeyOf({isolate, medium, temperatureC})   the set's key, or null while
 *                    one of the three is missing: an isolate's code, the
 *                    medium as plates store it (case and spaces aside), the
 *                    temperature to a tenth of a degree
 *   groupSets(items, keyOf)    sets in the order their first member comes,
 *                    lettered A, B, ... Z, AA, AB ...; each member's place in
 *                    its set (A1, A2, ...)
 *   summarise(values)          n, mean and the sample SD (n - 1; null under 2)
 *   setStats(members)          what a set's measured members say: n, clean
 *                    area and radius as mean ± SD, how many contaminated
 *   matchPlate(plates, {medium, temperatureC}, used)   the first of an
 *                    isolate's growing plates on that medium at that
 *                    temperature that no dish has yet
 *   setBands(curves, value)    a plate set's growth as one line, mean ± SD per
 *                    calendar day of reading (replicates read on one tray
 *                    share their day), each plate once a day: its latest
 *                    reading of that day, so n counts replicates, not readings
 */
//: The plate statuses a dish photographed today can be: started, not ended (routes/plates.py's plate_status).
export const GROWING = Object.freeze(['pending', 'ready_to_transfer', 'slow_growth', 'contaminated', 'in_rescue']);

export const normMedium = medium => String(medium ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

/** A temperature as a set's key says it: a tenth of a degree, or null for none. */
export function tempKey(value) {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const v = Number(value);
  return Number.isFinite(v) ? (Math.round(v * 10) / 10).toFixed(1) : null;
}

export function setKeyOf({ isolate, medium, temperatureC }) {
  const code = typeof isolate === 'string' ? isolate : isolate?.code;
  const m = normMedium(medium);
  const temp = tempKey(temperatureC);
  return code && m && temp !== null ? `${code}|${m}|${temp}` : null;
}

/** A, B, ... Z, AA, AB, ...: the nth set's letter (from 0). */
export function setLetter(n) {
  let i = n;
  let out = '';
  do {
    out = String.fromCharCode(65 + (i % 26)) + out;
    i = Math.floor(i / 26) - 1;
  } while (i >= 0);
  return out;
}

/** [{key, letter, index, members}] in first-member order; items with no key are in no set. */
export function groupSets(items, keyOf) {
  const sets = new Map();
  for (const item of items || []) {
    const key = keyOf(item);
    if (!key) continue;
    if (!sets.has(key)) sets.set(key, { key, letter: setLetter(sets.size), index: sets.size, members: [] });
    sets.get(key).members.push(item);
  }
  return [...sets.values()];
}

/** n, mean and sample SD of the finite values; SD null under two values, mean null under one. */
export function summarise(values) {
  const xs = (values || []).filter(v => typeof v === 'number' && Number.isFinite(v));
  const n = xs.length;
  if (!n) return { n: 0, mean: null, sd: null };
  const mean = xs.reduce((a, b) => a + b, 0) / n;
  if (n < 2) return { n, mean, sd: null };
  const sd = Math.sqrt(xs.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1));
  return { n, mean, sd };
}

/** A set's measured members ({clean, radius, contaminated}): n, mean ± SD of each, how many contaminated. */
export function setStats(members) {
  const measured = (members || []).filter(m => m && typeof m.clean === 'number');
  return {
    n: measured.length,
    clean: summarise(measured.map(m => m.clean)),
    radius: summarise(measured.map(m => m.radius)),
    contaminated: measured.filter(m => m.contaminated).length,
  };
}

/** The first growing plate of `plates` on that medium at that temperature, not in `used` (ids); else null. */
export function matchPlate(plates, { medium, temperatureC }, used = new Set()) {
  const m = normMedium(medium);
  const temp = tempKey(temperatureC);
  if (!m || temp === null) return null;
  return (plates || []).find(p => GROWING.includes(p.status) && !used.has(p.plate_id)
    && normMedium(p.protocol) === m && tempKey(p.temperature_c) === temp) || null;
}

/**
 * A set of plates' growth curves (routes/culture_analyses.py growth(): points with day,
 * observed_at, and the value `value(point)` reads) as one line: per calendar day of reading,
 * the mean day, n, mean and SD of the value, contaminated when one of them was.
 */
export function setBands(curves, value) {
  const byDate = new Map();
  for (const curve of curves || []) {
    // The plate's latest reading of each day: read again the same day, it is still one replicate.
    const latest = new Map();
    for (const p of curve.points || []) {
      const v = value(p);
      if (typeof v !== 'number' || !Number.isFinite(v)) continue;
      const date = String(p.observed_at || '').slice(0, 10);
      const held = latest.get(date);
      if (!held || String(p.observed_at || '') >= String(held.p.observed_at || '')) latest.set(date, { p, v });
    }
    for (const [date, { p, v }] of latest) {
      if (!byDate.has(date)) byDate.set(date, []);
      byDate.get(date).push({ day: p.day, v, contaminated: !!(p.contaminated ?? p.contamination_detected) });
    }
  }
  return [...byDate.entries()].map(([date, at]) => {
    const s = summarise(at.map(a => a.v));
    return { date, day: at.reduce((a, b) => a + b.day, 0) / at.length, n: s.n, mean: s.mean, sd: s.sd,
             contaminated: at.some(a => a.contaminated) };
  }).sort((a, b) => a.day - b.day || a.date.localeCompare(b.date));
}
